import type { FC } from 'react';

import { getStaticPrefix } from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import ComponentError from '@eneco/components/ui/ComponentError/ComponentError';
import { useFinancialsPublicV3GetPaymentTransactionStatusFromEncryptedQueryStringV3 } from '@eneco/dc/hooks';
import { useLinkComponent } from '@eneco/link';
import { Skeleton, Bucket, Heading, Stack, NotificationBox, Divider, ButtonLink, Image } from '@eneco/sparky';
import { NotificationBoxProps } from '@eneco/sparky/types';
import { useContent } from '@sitecore/common';
import { IDealPaymentV2Rendering } from '@sitecore/types/IDealPaymentV2';

import { TransactionStatus as TransactionStatusType } from '../PaymentTransactionStatus/PaymentTransactionStatus';


const IDEAL_LOGO_SIZE = '32px';

type NotificationFieldType = keyof Pick<
  IDealPaymentV2Rendering['fields'],
  'notificationSuccessField' | 'notificationErrorField' | 'notificationAlreadyPaidField'
>;

export const IDealTransactionResult: FC<{ transactionId: string; query: string }> = ({ transactionId, query }) => {
  const { f } = useContent<IDealPaymentV2Rendering>();
  const Link = useLinkComponent();
  const statusInfoMap: {
    [k in TransactionStatusType]: NotificationFieldType;
  } = {
    Success: 'notificationSuccessField',
    Cancelled: 'notificationErrorField',
    Expired: 'notificationErrorField',
    Open: 'notificationAlreadyPaidField',
    Error: 'notificationErrorField',
  };

  const { data, error } = useFinancialsPublicV3GetPaymentTransactionStatusFromEncryptedQueryStringV3({
    query,
    paymentId: transactionId,
  });

  if (error) {
    return <ComponentError />;
  }
  if (!data) {
    return <Skeleton height={200} />;
  }
  const { status } = data as { status: TransactionStatusType };
  const isTransactionSuccessful = status === 'Success';
  const isTransactionAlreadyPaid = status === 'Open';
  const isTransactionUnsuccessful = !isTransactionAlreadyPaid && !isTransactionSuccessful;

  const iDealLogoURL = `${getStaticPrefix()}${'/images/multilabel/ideal-wero.png'}`;

  const shouldShowViewInvoices = isTransactionSuccessful || isTransactionAlreadyPaid;

  return (
    <Bucket title={f('cardTitle.value')}>
      <Bucket.Content>
        <Stack gap="6">
          <Stack gap="2" direction="row" alignY="center">
            <Image src={iDealLogoURL} alt={'iDeal logo'} width={IDEAL_LOGO_SIZE} />
            {!isTransactionAlreadyPaid && (
              <Heading as="h1" size="XS">
                {isTransactionSuccessful ? f('successTitle.value') : (f('generalTitle.value') as string)}
              </Heading>
            )}
          </Stack>
          <NotificationBox
            isAlert={true}
            variant={f(`${statusInfoMap[status]}.value.variant`) as NotificationBoxProps['variant']}
            title={f(`${statusInfoMap[status]}.value.title`) as string}
            text={<RichText html={f(`${statusInfoMap[status]}.value.content`) as string} />}
          />
          {shouldShowViewInvoices && (
            <>
              <Divider />
              <Stack gap="2">
                <Heading as="h2" size="XS">
                  {f('viewInvoicesTitle.value') as string}
                </Heading>
                <RichText html={f('viewInvoicesDescription.value')} />
              </Stack>
            </>
          )}
        </Stack>
        {!isTransactionUnsuccessful && (
          <Bucket.Actions>
            <Link href={f('viewInvoicesLink.value.href')} linkType={f('viewInvoicesLink.value.linktype')}>
              <ButtonLink>{f('viewInvoicesLink.value.text')}</ButtonLink>
            </Link>
          </Bucket.Actions>
        )}
      </Bucket.Content>
    </Bucket>
  );
};
